import { z } from 'zod'

import {
  JEPX_VOLUME_CONSTRAINT,
  violatesJepxStep,
} from '~/models/numberInputConstraint'
import {
  JEPX_NOTE_MAX_LEN,
  isValidEmissionAlpha,
} from '~/pages/SpotBidding/models/emissionLinking'

/**
 * 数量 (MW) が保存できる刻みに載っているか検証する。
 *
 * 入力・表示・保持のすべてが MW (AC-BSP-053 / issue #4285) で、 VO `BidVolumeMw` /
 * DB `numeric(10,1)` の粒度 0.1 MW は制度刻みそのもの。 入力欄の `step` 属性は
 * 強制力が無いため、 schema 側でも止めて 400 になる前にフォーム内エラーとして返す。
 */
const volumeMwStepMessage = `数量は ${JEPX_VOLUME_CONSTRAINT.step} MW 単位で入力してください`

const isOnVolumeMwGrid = (mw: number): boolean =>
  !Number.isFinite(mw) || !violatesJepxStep(mw, JEPX_VOLUME_CONSTRAINT.step)

/**
 * 排出係数紐付け (spec spot-bidding.md §7) のフォーム入力検証を schema に足す。
 * ON のときアルファベットは大文字半角英字 (A〜Z) 1 文字必須 (AC-BSP-085)。
 * 判別コメント本体はモーダル側が composeEmissionComment で組み立てて
 * `memoJepx` に格納するため、 schema は UI 状態 2 field の整合のみを見る。
 */
export const emissionRefine = (
  v: { emissionLinked?: boolean; emissionAlpha?: string },
  ctx: z.RefinementCtx,
): void => {
  if (v.emissionLinked !== true) return
  const alpha = v.emissionAlpha ?? ''
  if (alpha === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['emissionAlpha'],
      message: 'アルファベット (A〜Z) を入力してください',
    })
    return
  }
  if (!isValidEmissionAlpha(alpha)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['emissionAlpha'],
      message: '大文字半角英字 (A〜Z) 1文字で入力してください',
    })
  }
}

/**
 * 個別作成・編集 ([[individualBidFormSchema]]) と一括作成系
 * ([[spotBidCreateFormSchema]]) が共有する メモ / 排出係数紐付け の入力定義。
 *
 * 同じ入力欄の文言・上限値を schema ごとに複製すると、 片方だけ直した時に
 * 画面ごとに別文言が出る (= spec §9 の文言 SoT に対して実装側の SoT が 2 つに割れる)。
 * `emissionRefine` と同じく定義を 1 箇所に置き、 両 schema へ spread する。
 */
const memoAndEmissionShape = {
  /**
   * JEPX自由記述欄 (memo_jepx、 任意、 70 文字以内 = eSLJ note の上限)。
   * JEPX へ note として送信される。 文言は spec §9 AC-BSP-061 の規定文言
   * (項目ラベル `biddingSpot.form.memoJepx` と同語) をそのまま使う。
   */
  memoJepx: z
    .string()
    .max(JEPX_NOTE_MAX_LEN, 'JEPX 自由記述欄は70文字以内で入力してください')
    .optional(),
  /** メモ（自由記述、 任意、 100 文字以内）。 社内限定 (JEPX へは送信しない)。 */
  memoFree: z
    .string()
    .max(100, 'メモは100文字以内で入力してください')
    .optional(),
  /** 排出係数紐付け ON/OFF (spec §7)。 UI 状態 (保存対象は組み立て後の memoJepx)。 */
  emissionLinked: z.boolean().optional(),
  /** 排出係数紐付けのアルファベット (A〜Z 1 文字、 ON のとき必須)。 */
  emissionAlpha: z.string().optional(),
}

/**
 * スポット入札 承認依頼 フォーム入力スキーマ。
 * React Hook Form + Zod で使用する。
 */
export const spotApprovalRequestFormSchema = z.object({
  /** 受渡日 (YYYY-MM-DD 形式の文字列 - フォームで日付 input を使う) */
  targetDate: z.string().min(1, '受渡日を入力してください'),
  /** 承認モード (enum number) */
  approvalMode: z.number().min(1, '承認モードを選択してください'),
})

export type SpotApprovalRequestFormInput = z.infer<
  typeof spotApprovalRequestFormSchema
>

/**
 * 個別作成 (1 コマ × 1 エリア × 1 方向) の札入力フォームスキーマ。
 * design `spot.html` `#bid-new-modal` の入力項目に対応。
 */
export const individualBidFormSchema = z
  .object({
    /** 売/買 (Side enum number)。 */
    direction: z
      .number({ error: '売/買を選択してください' })
      .int()
      .min(1, '売/買を選択してください'),
    /**
     * コマ番号 (1〜48)。
     *
     * 未選択は `enumSetValueAs` が `undefined` を返す (= 型チェックで落ちる) 経路なので
     * spec §9 AC-BSP-062 の「コマ未選択」文言は `z.number` の error 側に置く。
     * `.min` / `.max` に来るのは数値としての範囲外だけなので、 下限・上限とも
     * 「コマが 1〜48 の範囲外」の規定文言を返す。
     */
    timeCode: z
      .number({ error: 'コマを選択してください' })
      .int()
      .min(1, 'コマは 1〜48 の範囲で選択してください')
      .max(48, 'コマは 1〜48 の範囲で選択してください'),
    /** エリア (AreaCode enum number)。 */
    areaCode: z
      .number({ error: 'エリアを選択してください' })
      .int()
      .min(1, 'エリアを選択してください'),
    /**
     * 発注主体区分 (LicenseType enum number)。 入力欄は [[IssuerSelector]] で、 選んだ
     * 区分をそのまま書込の issuer_type として送る。
     */
    issuerType: z
      .number({ error: '発注主体を選択してください' })
      .int()
      .min(1, '発注主体を選択してください'),
    /** 入札価格 (円/kWh、 0.01 単位)。 成行 (orderMethod = MARKET) は 0 固定 (AC-BSP-004a)。 */
    priceYenPerKwh: z
      .number({ error: '価格を入力してください' })
      .min(0, '価格は 0 以上で入力してください'),
    /**
     * 入札量 (MW、 = 1 コマ 30 分の平均電力。 AC-BSP-053)。
     * 書込 contract も MW なので `formToBidInput` は換算しない。
     */
    volumeMw: z
      .number({ error: '数量を入力してください' })
      .min(0, '数量は 0 以上で入力してください')
      .refine(isOnVolumeMwGrid, volumeMwStepMessage),
    /**
     * 価格種別 (指値 / 成行、 AC-BSP-004a)。 `BiddingSpotOrderMethod` の wire 値
     * (1 = LIMIT / 2 = MARKET) をそのまま持つ。
     */
    orderMethod: z
      .number({ error: '価格種別を選択してください' })
      .int()
      .min(1, '価格種別を選択してください')
      .max(2, '価格種別を選択してください'),
    ...memoAndEmissionShape,
  })
  .superRefine(emissionRefine)

export type IndividualBidFormInput = z.infer<typeof individualBidFormSchema>

/**
 * 手数料自動考慮が ON の個別作成フォームの schema (AC-BSP-004b)。 価格欄は希望価格なので下限を置かない。
 * 新規作成・保存済みの札を開く経路 (編集・複製・再入札) を問わず、 希望価格そのものの負数・0 では止めず、
 * 算出した入札価格が値域・入札制限に入るかで判定する (#11466 Q117 = B / Q117-2。 売りの希望価格 -0.02 は
 * 手数料単価 0.03 で入札価格 0.01 になる)。 新規作成は backend のプレビュー ([[resolveSpotFeeSubmission]])、
 * 札を開く経路は送信時の値域判定が止める。
 */
export const desiredPriceIndividualBidFormSchema =
  individualBidFormSchema.safeExtend({
    priceYenPerKwh: z.number({ error: '価格を入力してください' }),
  })

/**
 * スポット入札 個別追加（コマ選択時に開く入札画面）フォーム入力スキーマ。
 * enum (licenseType / direction / areaCode) は Select の string 値を setValueAs で
 * number へ coerce する前提。 price / volume は文字列入力。
 */
export const spotBidCreateFormSchema = z
  .object({
    /**
     * 発注主体区分 (LicenseType enum number)。 [[individualBidFormSchema]] と同じ入力欄
     * ([[IssuerSelector]]) を共有し、 選んだ区分をそのまま書込の issuer_type として送る。
     */
    licenseType: z
      .number({ error: '発注主体を選択してください' })
      .min(1, '発注主体を選択してください'),
    /** 売/買 方向 (enum number) */
    direction: z
      .number({ error: '売/買を選択してください' })
      .min(1, '売/買を選択してください'),
    /** コマ番号 (1〜48) - コマリボンのクリックで初期値が入る */
    timeCode: z
      .number({ error: 'コマ番号は 1〜48 の範囲で入力してください' })
      .min(1, 'コマ番号は 1〜48 の範囲で入力してください')
      .max(48, 'コマ番号は 1〜48 の範囲で入力してください'),
    /** エリアコード (enum number) */
    areaCode: z
      .number({ error: 'エリアを選択してください' })
      .min(1, 'エリアを選択してください'),
    /** 数量 (MW、 = 1 コマ 30 分の平均電力。 AC-BSP-053) - 文字列で入力 */
    volumeMw: z
      .string()
      .min(1, '数量を入力してください')
      .refine((v) => isOnVolumeMwGrid(Number(v)), volumeMwStepMessage),
    /** 価格 (円/kWh) - 文字列で入力 */
    priceYenPerKwh: z.string().min(1, '価格を入力してください'),
    /**
     * 価格種別 (指値 / 成行、 AC-BSP-004a)。 `BiddingSpotOrderMethod` の wire 値
     * (1 = LIMIT / 2 = MARKET) をそのまま持つ。 [[individualBidFormSchema]] と同じ
     * 選択肢 (コマリボンから開く本モーダルも「個別作成」の一種、 AC-BSP-004a)。
     */
    orderMethod: z
      .number({ error: '価格種別を選択してください' })
      .int()
      .min(1, '価格種別を選択してください')
      .max(2, '価格種別を選択してください'),
    ...memoAndEmissionShape,
  })
  .superRefine(emissionRefine)

export type SpotBidCreateFormInput = z.infer<typeof spotBidCreateFormSchema>

/**
 * 差戻し理由入力フォームスキーマ (REJECT action 時)。
 */
export const rejectReasonFormSchema = z.object({
  reason: z.string().min(1, '差戻し理由を入力してください'),
})

export type RejectReasonFormInput = z.infer<typeof rejectReasonFormSchema>
